import { useEffect } from 'react';

interface OrderTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any
}

export default function OrderTrackingModal({ isOpen, onClose, order }: OrderTrackingModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const trackingSteps = [
    {
      status: 'Order Placed',
      date: order.date,
      completed: true,
      icon: 'ri-checkbox-circle-line',
      description: 'Your order has been placed successfully'
    },
    {
      status: 'Processing',
      date: order.status === 'Processing' || order.status === 'Shipped' || order.status === 'Delivered' ? order.date : '',
      completed: order.status === 'Processing' || order.status === 'Shipped' || order.status === 'Delivered',
      icon: 'ri-settings-3-line',
      description: 'Your order is being prepared'
    },
    {
      status: 'Shipped',
      date: order.status === 'Shipped' || order.status === 'Delivered' ? order.date : '',
      completed: order.status === 'Shipped' || order.status === 'Delivered',
      icon: 'ri-truck-line',
      description: 'Your order is on the way'
    },
    {
      status: 'Out for Delivery',
      date: order.status === 'Delivered' ? order.deliveredDate : '',
      completed: order.status === 'Delivered',
      icon: 'ri-map-pin-line',
      description: 'Your order is out for delivery'
    },
    {
      status: 'Delivered',
      date: order.deliveredDate || '',
      completed: order.status === 'Delivered',
      icon: 'ri-home-smile-line',
      description: 'Your order has been delivered'
    }
  ];

  const currentStepIndex = trackingSteps.findIndex(step => 
    step.status === order.status || 
    (order.status === 'Processing' && step.status === 'Processing') ||
    (order.status === 'Shipped' && step.status === 'Shipped') ||
    (order.status === 'Delivered' && step.status === 'Delivered')
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Track Order</h2>
            <p className="text-sm text-gray-600 mt-1">{order.VchNo}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        <div className="p-6">
          {order.Status === 'Cancelled' ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                <i className="ri-close-circle-line text-4xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Order Cancelled</h3>
              <p className="text-gray-600">This order has been cancelled</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-600 rounded-full">
                      <i className={`${'ri-map-pin-line'} text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        { order?.ObjItems[0]?.Status}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {""}
                      </p>
                    </div>
                  </div>
                  {order.StrDeliveryDt && order.Status !== 'Delivered' && (
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Expected Delivery:</span>{' '}
                        {new Date(order.StrDeliveryDt).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {trackingSteps.map((step, index) => (
                  <div key={index} className="relative">
                    {index !== trackingSteps.length - 1 && (
                      <div 
                        className={`absolute left-6 top-14 w-0.5 h-16 ${
                          step.completed ? 'bg-amber-600' : 'bg-gray-200'
                        }`}
                      ></div>
                    )}
                    
                    <div className="flex gap-4">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0 ${
                        step.completed 
                          ? 'bg-amber-600 text-white' 
                          : 'bg-gray-100 text-gray-400'
                      }`}>
                        <i className={`${step.icon} text-2xl`}></i>
                      </div>
                      
                      <div className="flex-1 pt-2">
                        <h4 className={`font-bold mb-1 ${
                          step.completed ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {step.status}
                        </h4>
                        <p className={`text-sm mb-1 ${
                          step.completed ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          {step.description}
                        </p>
                        {step.date && (
                          <p className="text-xs text-gray-500">
                            {new Date(step.date).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-information-line text-amber-600"></i>
                    Tracking Information
                  </h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>• Your order is being handled with care</p>
                    <p>• You will receive updates via email and SMS</p>
                    <p>• Contact support if you have any questions</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
